/*eslint-disable max-lines*/
import { Box, Typography } from '@truxweb/ux';
import { NextPageProps, TNextRedirect } from '../types';
import { useFullHeightStyles, useTextWebPage } from '../styles';
import { BasePage } from '../pageTemplates';
import { GetServerSidePropsContext } from 'next';
import { getTruxwebServerSideProps } from '../pageProps';

const PAGE_ID = 'privacy-policy';
const PAGE_LANGUAGE = 'en-CA';
const REQUIRED_NAMESPACES = ['common', PAGE_ID];

export const PrivacyPolicy = (): JSX.Element => {
  const classes = useFullHeightStyles();
  const styles = useTextWebPage();

  return (
    <BasePage pageId={PAGE_ID}>
      <Box className={classes.fullHeightContainer}>
        <Box mb={2} mt={2}>
          <Typography variant="h2">PRIVACY POLICY</Typography>
          <Typography variant="body1">
            <p>Last update: 2021-10-06</p>{' '}
            <p>
              This policy describes how Truxweb Inc. (“Truxweb”, “we“, “us“) collects, processes,
              and uses information and data about users of the Truxweb’s website and mobile
              application (together the “Platform”). The use of the second person (&quot;you&quot;,
              &quot;your&quot;, &quot;yours&quot;) refers to a user of the Platform or our client,
              depending on the context.
            </p>
            <p>
              {' '}
              The contact details of the person responsible for data processing under this policy
              are as follows:
              <p>
                {' '}
                <strong>Truxweb Inc.</strong> <br />
                400 Montfort St. <br />
                Montréal,
                <br /> Quebec <br />
                H3C 4J9 <br />
                Canada
                <br /> Attn: <a href="mailto:privacy@truxweb.com">privacy@truxweb.com</a>
              </p>
            </p>
          </Typography>
          <ol className={styles.topOl}>
            <Typography variant="h3">
              <li className={styles.li}>. Our Processing of Personal Data</li>{' '}
            </Typography>
            <Typography variant="body1">
              In this policy, the term “Personal Data” refers to any information that relates to an
              identified or identifiable living individual. To provide our services and allow you to
              use the Platform, we must collect and process some Personal Data. Our processing is
              based upon the following principles:{' '}
              <ul>
                <li> We must process some Personal Data in order to provide you with services; </li>
                <li>
                  We process Personal Data on behalf of our clients and pursuant to their
                  instructions. In most cases, our client is your employer, which will be better
                  suited than us to address your concerns about the processing of your Personal Data
                  through the Platform;{' '}
                </li>
                <li>
                  We process Personal Data for the purpose of our legitimate commercial interests
                  and that of our clients, when such processing is not likely to infringe on
                  fundamental rights or to cause significant harm to individuals.
                </li>
              </ul>
            </Typography>

            <Typography variant="h3">
              <li className={styles.li}>. Collected Personal Data </li>
            </Typography>
            <Typography variant="body1">
              <p>
                {' '}
                We collect and process the data described below in accordance with this policy.
              </p>
            </Typography>
            <ol className={styles.ol}>
              <Typography variant="h6">
                <li className={styles.li}> Data collected from the User</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We collect information that users provide to us voluntarily through the interface
                  of the Platform (for example, by completing a form) or otherwise. This information
                  may also be provided by a third party such as your employer.
                </p>
              </Typography>
              <Typography variant="body1">
                <p>This information may include the following:</p>
              </Typography>

              <table
                style={{
                  border: '1px solid black',
                  borderCollapse: 'collapse',
                  marginBottom: '20px',
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <tr>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Data Category </strong>
                    </Typography>
                  </th>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Purpose of processing </strong>
                    </Typography>
                  </th>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <ul>
                        <li>Name and surname; </li> <li>Physical address; </li>
                        <li>Email address </li>
                        <li>Phone number; </li>
                        <li>Gender; </li>
                        <li>Occupation; </li>
                      </ul>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        This data serves when you sign up for, purchase or use the Platform
                        services, consult with our customer service team, send us an email, post on
                        our blog, set up free trial and communicate with us in any way.
                      </p>{' '}
                      <p>
                        This data serves to communicate with you, such as to provide quotes to you,
                        bill and collect money owed to us, to send you system alert messages (ex:
                        planned outages, new features, version updates, releases, abuse warnings and
                        changes to our privacy policy), to communicate with our users about their
                        account and provide customer support, and to improve our services, audit and
                        analyze data to improve the Platform and our services.{' '}
                      </p>
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>With regard to our clients:</p>
                      <ul>
                        <li>Credit card information and banking account information; </li>
                      </ul>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Invoicing and payment.</p>{' '}
                      <p>
                        We use third parties for secure credit card transaction processing, and we
                        send billing information to those third parties to process your orders and
                        credit card payments. Whenever possible, credit card information (or
                        information for other payment types) is collected by a trusted payment
                        processor partner, and not collected by us.
                      </p>
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Any information you provide us through the Platform, by email or by phone.{' '}
                      </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        This data is used to better understand how you use the Platform and to
                        answer your questions, if any.
                      </p>
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Analytics data which may include: </p>
                      <ul>
                        <li>The operating system of your device; </li>
                        <li>The language of your device; </li>
                        <li>The IP address from which your device accesses the Platform; </li>
                        <li>
                          Browsing and clickthrough data such as the number of connections to the
                          Platform, the duration of a session, the date of connections to the
                          Platform, the functions used;{' '}
                        </li>
                        <li>Web page from which you accessed the Platform; </li>
                      </ul>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Analytics data is collected through the Google Analytics service for the
                        purpose of improving the Platform.{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
              </table>
              <Typography variant="h6">
                <li className={styles.li}> Data collected automatically</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We automatically collect certain information during your use of the Platform. This
                  data may be recorded each time you interact with the Platform and includes:
                </p>
              </Typography>

              <table
                style={{
                  border: '1px solid black',
                  borderCollapse: 'collapse',
                  marginBottom: '20px',
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <tr>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Data Category </strong>
                    </Typography>
                  </th>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Purpose of processing </strong>
                    </Typography>
                  </th>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Your computer’s IP address; </p>
                      <p>Your browser’s and operating system’s configuration; </p>
                      <p>
                        Your mobile device’s configuration when you access the Platform from a
                        mobile device;{' '}
                      </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        This data is used to personalize your experience on the Platform, for
                        example by loading an interface with a specific language or with certain
                        highlighted features.
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Data about your use of the Platform, such as pages viewed, use of features,
                        time spent on pages, events recorded;{' '}
                      </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use this data to analyze user behavior on the Platform and improve our
                        services.{' '}
                      </p>{' '}
                      <p>
                        We use this data to provide our clients with information about the use of
                        the Platform by their employees.
                      </p>
                    </Typography>
                  </td>
                </tr>
              </table>
              <Typography variant="h6">
                <li className={styles.li}> Data collected from the User</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  When you access the Platform, we collect data from analytics tools such as Google
                  Analytics. This data may include, for example, gender, age, interests. This data
                  is not associated with you personally and is transmitted to us in aggregate form.
                  You can deactivate Google Analytics on your browser through an add-on available at
                  this address:{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout">
                    https://tools.google.com/dlpage/gaoptout
                  </a>
                  .
                </p>{' '}
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Data from social networks</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We collect information from social networking sites or applications (Facebook,
                  LinkedIn, Twitter) when you interact with profiles that we operate on these social
                  networking sites or applications. These sites or applications are also governed by
                  their own policies relating to your personal data, which may differ from ours and
                  may be applicable.
                </p>{' '}
              </Typography>

              <Typography variant="h6">
                <li className={styles.li}> Tracking technology </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We and our service providers may use cookies and other tracking technologies, such
                  as pixels and web beacons, in order to improve the performance of the Platform and
                  your experience, analyze trends, administer the website, track users&apos;
                  movements around the website, serve targeted advertisements, and gather
                  demographic information about our user base as a whole. Cookies are small files
                  stored on your hard drive by the server on which the Platform is located that
                  allow us to recognize you and customize content when you use the Platform. Certain
                  cookies are automatically erased when you close your browser (session cookies)
                  while others are stored indefinitely (permanent cookies). If you prefer not to
                  have cookies recorded on your device, you must disable cookies in your browser
                  settings. Some features of the Platform may be entirely or partially disabled if
                  cookies are disabled.
                </p>{' '}
                <p>
                  You can control the use of cookies at the individual browser level. We use service
                  providers that partner with third parties to display advertising on our Platform
                  or to manage and serve our advertising on other sites. Our third-party partners
                  may use cookies or similar tracking technologies in order to provide you
                  advertising or other content based upon your browsing activities and interests. If
                  you wish to opt out of interest-based advertising, click
                  <a href="http://preferences-mgr.truste.com/">
                    http://preferences-mgr.truste.com/
                  </a>
                  . Please note that restricting cookie access does not mean you will stop receiving
                  ads, but it does mean that these ads would be generic ads (i.e.: not based on your
                  browsing history).
                </p>{' '}
              </Typography>
            </ol>
            <Typography variant="h3">
              <li className={styles.li}>. Purpose of Processing </li>{' '}
            </Typography>
            <Typography variant="body1">
              <p>We use collected Personal Data as set forth in the table above.</p>
            </Typography>
            <ol className={styles.ol}>
              <Typography variant="h6">
                <li className={styles.li}> Our role as a service provider</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  As part of our services, we process Personal Data on behalf or our clients for
                  purposes determined by them. To learn more about our processing of Personal Data
                  based on instructions from our clients, you must contact our client (generally
                  your employer) directly.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Legitimate commercial interests</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We process collected Personal Data for the purpose of our legitimate interests and
                  that of our clients, including to improve the Platform and to prevent fraud by
                  analyzing user behavior. We value your privacy. Except in response to specific
                  instructions from our client, our processing of Personal Data never involves the
                  monitoring or targeting of a specific individual nor the disclosure of Personal
                  Data about a specific individual.
                </p>
              </Typography>

              <Typography variant="h6">
                <li className={styles.li}> Contacting You</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We use the Personal Data you provided us (for example your name and email address)
                  to contact you about your use of the Platform, but only if you have provided such
                  data and if you agree to receive communications from us. We abide by applicable
                  regulations with respect to unwanted electronic communications. If you no longer
                  wish to receive electronic communications from us, you can notify us at any time
                  at the e-mail address provided at the beginning of this policy.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Platform Maintenance</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We use data collected through the Platform and data collected from analytics tools
                  to monitor usage of the Platform generally, to prevent misuse of the Platform, to
                  identify problems or bugs in the Platform and to determine which features we
                  should improve.
                </p>
              </Typography>
            </ol>
            <Typography variant="h3">
              <li className={styles.li}>. Storage and Security </li>{' '}
            </Typography>
            <Typography variant="body1">
              <p>
                We store the collected Personal Data until our client asks us to destroy it or until
                our activities no longer require it to be stored. However, we do not make any
                commitment to store collected Personal Data for a specific period.
              </p>
              <p>
                {' '}
                Collected Personal Data is stored electronically on servers operated by our hosting
                provider, Amazon AWS. Data is stored on servers located in Canada.{' '}
              </p>
              <p>
                Our employees are informed of the confidential nature of Personal Data and undertake
                necessary training to handle such Personal Data in accordance with this policy.{' '}
              </p>
              <p>
                Collected Personal Data about individuals outside Canada and the United States may
                be transferred to Canada or the United States.{' '}
              </p>
              <p>
                Certain data relating to the use of the Platform and payments are processed by our
                service providers through facilities that may be located in jurisdictions other than
                Canada.{' '}
              </p>
              <p>
                Data generated from Google Analytics is stored on servers controlled by Google.{' '}
              </p>
            </Typography>
            <Typography variant="h3">
              <li className={styles.li}>. Disclosure of Personal Data </li>{' '}
            </Typography>
            <Typography variant="body1">
              <p>
                We only disclose your Personal Data in accordance with this policy or if we have
                your consent. Your Personal Data could be disclosed to the categories of recipients
                listed below for the purposes described.{' '}
              </p>
            </Typography>
            <ol className={styles.ol}>
              <Typography variant="h6">
                <li className={styles.li}> Your Employer </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  Your employer may access Personal Data recorded on the Platform. Some Personal
                  Data may be shared within your organization as determined by your employer or
                  administrative users, as the case may be. The Platform allows the export of
                  customer data, which could contain Personal Data.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Service Providers</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We share some Personal Data with our service providers in order to better offer
                  our services. Our service providers agree in writing to keep Personal Data
                  confidential and to use same solely to provide us with their services. Personal
                  Data under the control of our clients is not shared with our service providers
                  without the client’s consent.
                </p>
                <p>
                  Currently, we share Personal Data with the following service providers. Service
                  providers are subject to change at our discretion.{' '}
                </p>
              </Typography>
              <table
                style={{
                  border: '1px solid black',
                  borderCollapse: 'collapse',
                  marginBottom: '20px',
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <tr>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Supplier </strong>
                    </Typography>
                  </th>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Details </strong>
                    </Typography>
                  </th>
                  <th style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <strong>Useful Links </strong>
                    </Typography>
                  </th>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Google (Google, LLC) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of Google Maps and Google Analytics on the Platform. We
                        use Google&apos;s services in order to analyze usage of the Platform and to
                        personalize our advertising campaigns according to Platform traffic. We use
                        Google Maps to display digital maps and to calculate the distance between
                        different addresses. Google Analytics obtains information about the behavior
                        of users of the Platform, including through &quot;cookies&quot; which allow
                        Google to collect information about certain events such as the pages you
                        visit, the length of a session or the products you view. The data processed
                        by Google Analytics through the Platform allows us to personalize
                        advertising campaigns on the Google advertising network according to these
                        behaviors.
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Google Privacy Policy:{' '}
                        <a href="https://policies.google.com/privacy?hl=fr">
                          https://policies.google.com/privacy?hl=fr
                        </a>{' '}
                        Google&apos;s use of data from third-party sites:
                        <a href="www.google.com/policies/privacy/partners/ ">
                          www.google.com/policies/privacy/partners/
                        </a>{' '}
                        Deactivation tool:
                        <a href="https://tools.google.com/dlpage/gaoptout ">
                          {' '}
                          https://tools.google.com/dlpage/gaoptout
                        </a>
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>AWS (Amazon Web Services, Inc.) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of AWS to host collected Personal Data. AWS uses
                        industry-standard measures to ensure that our data and your Personal Data is
                        kept secure.
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        AWS Privacy Policy:{' '}
                        <a href="https://aws.amazon.com/privacy/?nc1=h_ls  ">
                          https://aws.amazon.com/privacy/?nc1=h_ls
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Bambora (Bambora Inc.) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of Bambora to process payments on the Platform. Bambora
                        receives the necessary information for payment by credit card.
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Bambora Privacy Policy:{' '}
                        <a href="https://www.bambora.com/en/ca/terms-conditions/    ">
                          https://www.bambora.com/en/ca/terms-conditions/
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Elavon, Inc</p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>We use the services of Elavonto process payment on the Platform.</p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Elavon Privacy Policy:{' '}
                        <a href="https://www.merchantconnect.com/CWRWeb/PrivacyCAEN.do ">
                          https://www.merchantconnect.com/CWRWeb/PrivacyCAEN.do
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>HubSpot, Inc. </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>We use the services of Hubspot for advertising services.</p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Hubspot Privacy Policy:{' '}
                        <a href="https://legal.hubspot.com/product-privacy-policy">
                          https://legal.hubspot.com/product-privacy-policy
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>MailChimp (The Rocket Science Group LLC d/b/a MailChimp) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of MailChimp to record each subscriber’s email address,
                        IP address, date, and time associated with each open and click for a
                        campaign. We use this data to create reports for our marketing team to learn
                        about how an email campaign performed and what actions subscribers took.{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        MailChimp Privacy Policy:{' '}
                        <a href="https://mailchimp.com/legal/privacy/  ">
                          https://mailchimp.com/legal/privacy/
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Marsh Ltd (d.b.a. Marsh Cargo) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of Marsh to provide you with different insurance
                        solutions.{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Marsh Privacy Policy
                        <a href="https://www.marsh.com/uk/privacy-notice.html ">
                          https://www.marsh.com/uk/privacy-notice.html
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Project 44, Inc. (d.b.a. project44) </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>We use the services of project44 to track the carrier’s vehicles. </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        project44 Privacy Policy:{' '}
                        <a href="https://www.project44.com/policies ">
                          https://www.project44.com/policies
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>RMIS – Registry Monitoring Insurance Services </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of RMIS to carry out a vetted compliance check (due
                        diligence) of all carriers that are listed and operate on the Platform.{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        RMIS Privacy Policy:{' '}
                        <a href="hhttps://rmis.com/Home/PrivacyPolicy ">
                          https://rmis.com/Home/PrivacyPolicy
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>Breezy.HR </p>
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        We use the services of Breezy.HR to post and receive applications for
                        hiring, testimonials and career opportunity
                      </p>{' '}
                    </Typography>
                  </td>
                  <td style={{ border: '1px solid black' }}>
                    <Typography variant="body1">
                      <p>
                        Breezy Privacy Policy:{' '}
                        <a href="https://breezy.hr/policies/privacy ">
                          https://breezy.hr/policies/privacy
                        </a>{' '}
                      </p>{' '}
                    </Typography>
                  </td>
                </tr>
              </table>
              <Typography variant="h6">
                <li className={styles.li}> Clients</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We share data in an aggregated form which does not identify specific individuals
                  with clients and potential clients for marketing purposes. This data includes
                  statistics about the Platform users and their behavior while using the Platform.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Legal Obligations </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We could disclose Personal Data if such disclosure is expressly authorized by law
                  or pursuant to an order by a competent authority. We may disclose Personal Data in
                  the context of judicial proceedings if necessary to protect our rights or that of
                  our users.
                </p>
              </Typography>

              <Typography variant="h6">
                <li className={styles.li}> Sale of Business </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  In the event that the sale or restructuring of the business operated by Truxweb to
                  a third party not controlled by Truxweb is envisioned (a &quot;Transaction&quot;),
                  we may not disclose collected Personal Data to the individuals or organizations
                  involved before and after the Transaction, whether the Transaction is finalized or
                  not, without the written authorization of your employer or your client, as the
                  case may be, who has granted you the right to use this Platform. If such
                  authorization is obtained, these individuals or organizations shall make a
                  commitment to us to maintain the confidentiality of the disclosed Personal Data
                  and to use it exclusively to assess the feasibility or desirability of the
                  Transaction, or in accordance with this policy.
                </p>
              </Typography>
            </ol>
            <Typography variant="h3">
              <li className={styles.li}>. Your Rights </li>{' '}
            </Typography>

            <ol className={styles.ol}>
              <Typography variant="h6">
                <li className={styles.li}> Access to Personal Data</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  You can access some Personal Data you provided us by accessing your user account
                  on the Platform. You may also request access to Personal Data that we hold about
                  you in writing at the e-mail address or postal address indicated at the beginning
                  of this policy. To access or modify some Personal Data recorded using the
                  Platform, you must contact your employer directly.{' '}
                </p>
                <p>
                  We will respond to your written request within 30 days of its receipt. We will
                  allow you reasonable access to your Personal Data contained in our records in
                  accordance with applicable law. We may direct you to your employer in cases where
                  your Personal Data is kept and processed by Truxweb on behalf of your employer.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Withdrawal of Consent</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  You may withdraw your consent to the processing of some Personal Data, where such
                  processing is based on your consent. If your consent is provided through your
                  employer, you must contact your employer directly to withdraw your consent.
                </p>
                <p>
                  If you wish to withdraw your consent to the processing of Personal Data over and
                  above what is possible through the Platform interface or your device settings, you
                  may notify us in writing at the e-mail address indicated at the beginning of this
                  policy. The only way to stop all processing of your Personal Data is to stop using
                  the Platform entirely.
                </p>
              </Typography>

              <Typography variant="h6">
                <li className={styles.li}> Complaints</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  This policy is governed by the laws and regulations in force in Canada and the
                  Canadian authorities shall be exclusively competent to hear complaints based on
                  this policy or regarding the processing of your Personal Data by Truxweb.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Identity Verification </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  We may verify the identity of a person who wishes to access Personal Data.
                  Information provided to verify identity will not be used for other purposes.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Amendment of Policy </li>
              </Typography>

              <Typography variant="body1">
                <p>
                  This policy may be amended to reflect changes in our Personal Data processing
                  practices. If an amendment is made, the new policy will be available using the
                  Platform and on our website.{' '}
                </p>
                <p>
                  We will notify you of any material changes in this policy by email prior to the
                  effective policy change. Any notice will be sent to the email address you provided
                  in the Platform or posted directly on the Platform.
                </p>
              </Typography>
              <Typography variant="h6">
                <li className={styles.li}> Additional information</li>
              </Typography>

              <Typography variant="body1">
                <p>
                  For additional information on how we handle User Data, including confidential
                  information and Personal Data, you may contact us using contact information
                  provided at the beginning of this policy.
                </p>
              </Typography>
            </ol>
          </ol>
        </Box>
      </Box>
    </BasePage>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<NextPageProps | TNextRedirect> => {
  return await getTruxwebServerSideProps(
    context,
    {
      pageId: PAGE_ID,
      pageLanguage: PAGE_LANGUAGE,
      requiredNamespaces: REQUIRED_NAMESPACES,
    },
    true
  );
};

export default PrivacyPolicy;
